import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Rocket, Save, Play, UserPlus, StopCircle, Mail, MessageSquare } from "lucide-react";

const CHANNELS = [
  { v: "both", l: "Courriel + SMS" },
  { v: "email", l: "Courriel" },
  { v: "sms", l: "SMS" },
];

export default function OnboardingSequenceModule() {
  const [steps, setSteps] = useState([]);
  const [twilio, setTwilio] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [kpis, setKpis] = useState({});
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get("/onboarding/steps").then((r) => { setSteps(r.data.steps); setTwilio(r.data.twilio_configured); }).catch(() => {});
    api.get("/onboarding/enrollments").then((r) => { setEnrollments(r.data.enrollments); setKpis(r.data.kpis); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStep = (i, field, val) => setSteps((s) => s.map((st, idx) => idx === i ? { ...st, [field]: val } : st));

  const saveSteps = async () => {
    setSaving(true);
    try { await api.put("/onboarding/steps", { steps }); toast.success("Séquence enregistrée."); load(); }
    catch { toast.error("Échec de l'enregistrement."); }
    setSaving(false);
  };

  const enroll = async () => {
    if (!form.full_name.trim()) { toast.error("Nom requis."); return; }
    try { await api.post("/onboarding/enroll", form); toast.success(`${form.full_name} inscrit(e) à la séquence.`); setForm({ full_name: "", email: "", phone: "" }); load(); }
    catch { toast.error("Échec de l'inscription."); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/onboarding/run");
      toast.success(`Séquence lancée : ${r.data.sent} message(s) envoyé(s) sur ${r.data.processed} inscrit(s).`);
      load();
    } catch { toast.error("Échec du lancement."); }
    setRunning(false);
  };

  const stop = async (id) => { await api.post(`/onboarding/enrollments/${id}/stop`); toast.success("Séquence arrêtée."); load(); };

  return (
    <div className="p-6 space-y-6 text-zinc-100" data-testid="onboarding-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Rocket className="w-6 h-6 text-indigo-400" /> Séquence d'accueil (Onboarding)</h1>
          <p className="text-sm text-zinc-400 mt-1">Messages automatiques SMS + courriel pour accueillir et convertir les nouveaux membres.</p>
        </div>
        <button onClick={runNow} disabled={running} data-testid="onboarding-run" className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium flex items-center gap-1 disabled:opacity-60"><Play className="w-4 h-4" /> {running ? "Envoi…" : "Lancer la séquence maintenant"}</button>
      </div>

      {!twilio && <div className="text-xs text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">⚠️ SMS (Twilio) non configuré — seuls les courriels seront envoyés (Gmail connecté requis).</div>}

      {/* Éditeur d'étapes */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-zinc-200">Étapes de la séquence</h2>
          <button onClick={saveSteps} disabled={saving} data-testid="onboarding-save" className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-medium flex items-center gap-1 disabled:opacity-60"><Save className="w-4 h-4" /> {saving ? "…" : "Enregistrer"}</button>
        </div>
        {steps.map((s, i) => (
          <div key={s.key || i} className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 space-y-2" data-testid={`onboarding-step-${i}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-xs text-zinc-400 flex items-center gap-1">Jour J+<input type="number" min="0" value={s.delay_days} onChange={(e) => updateStep(i, "delay_days", parseInt(e.target.value) || 0)} className="w-16 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm" /></label>
              <select value={s.channel} onChange={(e) => updateStep(i, "channel", e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm">
                {CHANNELS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
              <label className="text-xs text-zinc-400 flex items-center gap-1 ml-auto"><input type="checkbox" checked={s.enabled} onChange={(e) => updateStep(i, "enabled", e.target.checked)} /> Actif</label>
            </div>
            <input value={s.subject} onChange={(e) => updateStep(i, "subject", e.target.value)} placeholder="Objet du courriel" className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
            <textarea value={s.body} onChange={(e) => updateStep(i, "body", e.target.value)} rows={2} placeholder="Message ({prenom} = prénom du membre)" className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
          </div>
        ))}
        <p className="text-[11px] text-zinc-500">Astuce : utilisez <code className="text-indigo-300">{"{prenom}"}</code> pour insérer le prénom du membre.</p>
      </div>

      {/* Inscrire un membre */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-3">
        <h2 className="text-sm font-bold text-zinc-200">Inscrire un nouveau membre</h2>
        <div className="grid sm:grid-cols-3 gap-2">
          <input value={form.full_name} onChange={(e) => setForm((s) => ({ ...s, full_name: e.target.value }))} placeholder="Nom complet" data-testid="enroll-name" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
          <input value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} placeholder="Courriel" data-testid="enroll-email" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
          <input value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} placeholder="Téléphone (+1...)" data-testid="enroll-phone" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
        </div>
        <button onClick={enroll} data-testid="enroll-btn" className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium flex items-center gap-1"><UserPlus className="w-4 h-4" /> Inscrire à la séquence</button>
      </div>

      {/* Inscrits */}
      <div className="rounded-xl border border-zinc-800 overflow-hidden">
        <div className="px-4 py-3 bg-zinc-900/80 text-sm flex gap-4">
          <span className="text-zinc-300">Inscrits : <b>{kpis.total || 0}</b></span>
          <span className="text-emerald-400">Actifs : <b>{kpis.actifs || 0}</b></span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-zinc-500 text-xs uppercase">
            <tr><th className="text-left px-4 py-2">Membre</th><th className="text-left px-4 py-2">Contact</th><th className="text-left px-4 py-2">Étapes envoyées</th><th className="text-left px-4 py-2">Statut</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody>
            {enrollments.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-zinc-500" data-testid="onboarding-empty">Aucun inscrit. Ajoutez-en un ci-dessus.</td></tr>}
            {enrollments.map((e) => (
              <tr key={e.id} data-testid={`enrollment-${e.id}`} className="border-t border-zinc-800/70">
                <td className="px-4 py-2">{e.full_name}</td>
                <td className="px-4 py-2 text-xs text-zinc-400">{e.email && <span className="inline-flex items-center gap-1 mr-2"><Mail className="w-3 h-3" />{e.email}</span>}{e.phone && <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{e.phone}</span>}</td>
                <td className="px-4 py-2 text-xs text-zinc-300">{(e.sent_steps || []).length} / {steps.length}</td>
                <td className="px-4 py-2">{e.active ? <span className="text-emerald-400 text-xs">Active</span> : <span className="text-zinc-500 text-xs">Arrêtée</span>}</td>
                <td className="px-4 py-2 text-right">{e.active && <button onClick={() => stop(e.id)} data-testid={`stop-${e.id}`} className="text-red-300 hover:text-red-200 text-xs flex items-center gap-1 ml-auto"><StopCircle className="w-3.5 h-3.5" /> Arrêter</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
